import {
  IonButton,
  IonCard,
  IonCardContent,
  IonCardHeader,
  IonContent,
  IonIcon,
  IonItem,
  IonList,
  IonRow,
  useIonPopover,
} from '@ionic/react';
import { chevronDown, informationCircle } from 'ionicons/icons';
import React, { useMemo, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { assertNever } from 'src/utils/assert';
import { JobLogV2Data } from 'src/utils/logs/types';
import autoStrokes from '../../assets/images/auto_strokes_info.png';
import depthCleaned from '../../assets/images/depth_cleaned_info.png';
import lanceSpeed from '../../assets/images/lance_speed_info.png';
import { JobMessages } from '../../intl/JobMessages';
import { DqText } from '../dq-text';
import './TubeSheetCard.scss';
import TubeSheetGraph from './TubeSheetGraph';
import HeatMapModal from './information/information-modals/HeatMapModal';
import InstructionsModal from './information/information-modals/InstructionsModal';
import TubeSheetModal from './information/information-modals/TubeSheetModal';
import { TubeSheetChartType } from './util/types';

interface TubeSheetCardProps {
  data: JobLogV2Data;
  /** Time range min value. */
  timeRangeMin: number;
  /** Time range max value. */
  timeRangeMax: number;
  /** Whether to hide timestamps. */
  showTimeDetails: boolean;
  /** The timezone associated with the job log. */
  timezone: string;
  /** Allows the card to render with a preselected tube sheet chart type. */
  preselectedTubeSheetType?: TubeSheetChartType;
}

interface DropdownMenuProps {
  setChart: (chart: TubeSheetChartType) => void;
}

const DropdownMenu: React.FC<DropdownMenuProps> = props => {
  const { formatMessage } = useIntl();

  return (
    <IonContent>
      <IonList class="tube-sheet-dropdown-list">
        <IonItem onClick={() => props.setChart(TubeSheetChartType.MAIN)}>
          {formatMessage(JobMessages.tubeSheetTitle)}
        </IonItem>
        <IonItem onClick={() => props.setChart(TubeSheetChartType.HEAT)}>
          {formatMessage(JobMessages.tubeSheetHeatMapTitle)}
        </IonItem>
        <IonItem onClick={() => props.setChart(TubeSheetChartType.DEPTH)}>
          {formatMessage(JobMessages.tubeSheetDepthTitle)}
        </IonItem>
        <IonItem onClick={() => props.setChart(TubeSheetChartType.AUTOSTROKES)}>
          {formatMessage(JobMessages.tubeSheetAutoStrokesTitle)}
        </IonItem>
        <IonItem onClick={() => props.setChart(TubeSheetChartType.LANCESPEED)}>
          {formatMessage(JobMessages.tubeSheetLanceSpeedTitle)}
        </IonItem>
      </IonList>
    </IonContent>
  );
};

const TubeSheetCard: React.FC<TubeSheetCardProps> = props => {
  const { formatMessage } = useIntl();
  const [helpDialogOpen, setHelpDialogOpen] = useState<boolean>(false);
  const [currentChart, setCurrentChart] = useState(props.preselectedTubeSheetType ?? TubeSheetChartType.MAIN);
  const [present, dismiss] = useIonPopover(DropdownMenu, {
    setChart: (newChart: TubeSheetChartType) => {
      setCurrentChart(newChart);
      setHelpDialogOpen(false);
      dismiss();
    },
  });

  const instructionInfo = useMemo(() => {
    switch (currentChart) {
      case TubeSheetChartType.DEPTH:
        return {
          title: formatMessage(JobMessages.depthCleanedInfoTitle),
          bodyText: formatMessage(JobMessages.depthCleanedInfoBody),
          image: depthCleaned,
        };
      case TubeSheetChartType.AUTOSTROKES:
        return {
          title: formatMessage(JobMessages.autoStrokesInfoTitle),
          bodyText: formatMessage(JobMessages.autoStrokesInfoBody),
          image: autoStrokes,
        };
      case TubeSheetChartType.LANCESPEED:
        return {
          title: formatMessage(JobMessages.lanceSpeedInfoTitle),
          bodyText: formatMessage(JobMessages.lanceSpeedInfoBody),
          image: lanceSpeed,
        };
      default:
        return undefined;
    }
  }, [currentChart, formatMessage]);

  return (
    <IonCard>
      <IonCardHeader>
        <IonRow className="ion-justify-content-between ion-align-items-center">
          <div className="ion-flex ion-flex-direction-row ion-align-items-center">
            <DqText variant="h3">
              <TubeSheetCardHeader chart={currentChart} />
            </DqText>
            <IonButton fill="clear" onClick={(event: any) => present({ event })}>
              <IonIcon slot="icon-only" icon={chevronDown} />
            </IonButton>
          </div>

          <IonButton
            className="no-print"
            fill="clear"
            color="primary"
            onClick={() => {
              setHelpDialogOpen(true);
            }}
          >
            <IonIcon slot="icon-only" icon={informationCircle} />
          </IonButton>
        </IonRow>
      </IonCardHeader>
      <IonCardContent>
        <TubeSheetGraph
          tubeSheet={props.data.tubeSheet}
          timeTable={props.data.timeTable}
          timeRangeMin={props.timeRangeMin}
          timeRangeMax={props.timeRangeMax}
          autoMove={props.data.configuration.autoMove}
          firmware={props.data.firmwareVersion}
          showTimeDetails={props.showTimeDetails}
          chartType={currentChart}
          timezone={props.timezone}
        />
      </IonCardContent>
      <TubeSheetModal
        isOpen={helpDialogOpen && currentChart === TubeSheetChartType.MAIN}
        onClose={() => setHelpDialogOpen(false)}
      />
      <HeatMapModal
        isOpen={helpDialogOpen && currentChart === TubeSheetChartType.HEAT}
        onClose={() => setHelpDialogOpen(false)}
      />
      {instructionInfo && (
        <InstructionsModal
          isOpen={helpDialogOpen}
          onClose={() => setHelpDialogOpen(false)}
          bodyText={instructionInfo.bodyText}
          image={instructionInfo.image}
          title={instructionInfo.title}
        />
      )}
    </IonCard>
  );
};

const TubeSheetCardHeader = (props: { chart: TubeSheetChartType }) => {
  switch (props.chart) {
    case TubeSheetChartType.HEAT:
      return <FormattedMessage {...JobMessages.tubeSheetHeatMapTitle} />;
    case TubeSheetChartType.MAIN:
      return <FormattedMessage {...JobMessages.tubeSheetTitle} />;
    case TubeSheetChartType.DEPTH:
      return <FormattedMessage {...JobMessages.tubeSheetDepthTitle} />;
    case TubeSheetChartType.AUTOSTROKES:
      return <FormattedMessage {...JobMessages.tubeSheetAutoStrokesTitle} />;
    case TubeSheetChartType.LANCESPEED:
      return <FormattedMessage {...JobMessages.tubeSheetLanceSpeedTitle} />;
    default:
      assertNever(props.chart);
  }
};

export default TubeSheetCard;
